import { Ref, ref, ComputedRef, computed } from 'vue'
import { useLoading, Loading } from '@/composable/useLoading'
import { VehicleWithdrawalDepot } from '@/types/vehicle-withdrawal-depot'
import { getVehicleWithdrawalDepots } from '@/api/vehicle-withdrawal-depot/getVehicleWithdrawalDepots'
import { deleteVehicleWithdrawalDepot } from '@/api/vehicle-withdrawal-depot/deleteVehicleWithdrawalDepot'
import { createVehicleWithdrawalDepot } from '@/api/vehicle-withdrawal-depot/createVehicleWithdrawalDepot'
import { updateVehicleWithdrawalDepot } from '@/api/vehicle-withdrawal-depot/updateVehicleWithdrawalDepot'

interface UseVehicleWithdrawalDepot {
  data: Ref<VehicleWithdrawalDepot[]>
  fetchAll: () => Promise<VehicleWithdrawalDepot[]>
  remove: (uuid: string) => Promise<null>
  add: (body: VehicleWithdrawalDepot) => Promise<VehicleWithdrawalDepot>
  update: (body: VehicleWithdrawalDepot) => Promise<VehicleWithdrawalDepot>
  loading: ComputedRef<Loading>
}

const data = ref<VehicleWithdrawalDepot[]>([])
const { state } = useLoading()

export function useVehicleWithdrawalDepot(
  initFetchAll?: boolean
): UseVehicleWithdrawalDepot {
  async function fetchAll() {
    state.getAll = true
    return new Promise<VehicleWithdrawalDepot[]>((resolve, reject) => {
      getVehicleWithdrawalDepots()
        .then(({ data: vehicleWithdrawalDepots }) => {
          data.value = vehicleWithdrawalDepots
          resolve(vehicleWithdrawalDepots)
        })
        .catch(reject)
        .finally(() => {
          state.getAll = false
        })
    })
  }

  async function remove(uuid: string) {
    state.delete = true
    return new Promise<null>((resolve, reject) => {
      deleteVehicleWithdrawalDepot(uuid)
        .then(() => {
          data.value = data.value.filter((item) => item.uuid !== uuid)
          resolve(null)
        })
        .catch(reject)
        .finally(() => {
          state.delete = false
        })
    })
  }

  async function add(body: VehicleWithdrawalDepot) {
    state.create = true
    return new Promise<VehicleWithdrawalDepot>((resolve, reject) => {
      createVehicleWithdrawalDepot(body)
        .then(({ data: vehicleWithdrawlDepot }) => {
          data.value.push(vehicleWithdrawlDepot)
          resolve(vehicleWithdrawlDepot)
        })
        .catch(reject)
        .finally(() => {
          state.create = false
        })
    })
  }

  async function update(body: VehicleWithdrawalDepot) {
    state.update = true
    return new Promise<VehicleWithdrawalDepot>((resolve, reject) => {
      updateVehicleWithdrawalDepot(body)
        .then(({ data: vehicleWithdrawlDepot }) => {
          data.value = data.value.map((item) => {
            if (item.uuid === vehicleWithdrawlDepot.uuid) {
              return {
                ...item,
                ...vehicleWithdrawlDepot,
              }
            }

            return item
          })
          resolve(vehicleWithdrawlDepot)
        })
        .catch(reject)
        .finally(() => {
          state.update = false
        })
    })
  }

  if (initFetchAll) {
    fetchAll()
  }

  return {
    data,
    fetchAll,
    add,
    remove,
    update,
    loading: computed(() => state),
  }
}
